// Internal linking graph — Phase I
// Cross-section related-content engine: services ↔ blog ↔ industries.
// Pure data, no React. Keep deterministic to avoid hydration mismatch.

import type { ServiceId } from "@/data/services";
import { SERVICE_INDEX, type ServiceCardMeta } from "@/data/services-index.gen";

/** Light service shape used by internal-linking UI. */
export type ServiceMeta = ServiceCardMeta;
import { BLOG_INDEX, type BlogCardMeta, type BlogCategory } from "@/data/blog-index.th.gen";

/** Light post shape used by internal-linking UI (no article bodies shipped). */
export type BlogPost = BlogCardMeta;

/** Curated industry index — slugs/names only (full data lives in /routes/industries.tsx) */
export const INDUSTRY_INDEX: Array<{
  slug: string;
  name: string;
  tagline: string;
  serviceIds: ServiceId[];
  tagMatch: string[];
}> = [
  { slug: "manufacturing", name: "Manufacturing & Industrial", tagline: "BOI · ISO · Export",
    serviceIds: ["work-permit-boi", "trademark-ip", "translation", "mfa-legalization"],
    tagMatch: ["boi", "factory", "export", "iso"] },
  { slug: "fintech-banking", name: "Fintech & Banking", tagline: "PDPA · BOT · KYC",
    serviceIds: ["pdpa-cybersecurity", "banking-finance", "sworn-translation", "ma-private-equity"],
    tagMatch: ["fintech", "bank", "pdpa", "fatca", "crs", "kyc"] },
  { slug: "hospitality-tourism", name: "Hospitality & Tourism", tagline: "Hotel · Tour · F&B",
    serviceIds: ["work-permit-boi", "marriage-thai-foreign", "labor-employment", "translation"],
    tagMatch: ["hotel", "tourism", "hospitality"] },
  { slug: "healthcare-pharma", name: "Healthcare & Pharma", tagline: "FDA · Clinical · Records",
    serviceIds: ["naati", "sworn-translation", "notary-public", "apostille"],
    tagMatch: ["medical", "pharma", "clinical", "health"] },
  { slug: "education-academia", name: "Education & Academia", tagline: "Transcripts · Diplomas",
    serviceIds: ["translation", "mfa-legalization", "apostille", "visa-thai-citizens"],
    tagMatch: ["transcript", "diploma", "education", "wes", "iqas", "study"] },
  { slug: "real-estate", name: "Real Estate & Construction", tagline: "Foreign Ownership · Lease",
    serviceIds: ["real-estate-conveyancing", "notary-public", "translation", "tax-law"],
    tagMatch: ["real-estate", "land", "lease", "condo"] },
  { slug: "tech-saas", name: "Tech & SaaS", tagline: "IP · DTV · Remote Work",
    serviceIds: ["trademark-ip", "pdpa-cybersecurity", "visa-foreigners-thailand", "ltr-elite-visa"],
    tagMatch: ["dtv", "remote", "saas", "tech", "ltr"] },
  { slug: "legal-firms", name: "Legal Firms & Co-counsel", tagline: "Court · Litigation Support",
    serviceIds: ["sworn-translation", "criminal-defense", "international-arbitration", "notary-public"],
    tagMatch: ["court", "litigation", "arbitration", "sworn"] },
  { slug: "logistics-customs", name: "Logistics & Trade", tagline: "Customs · Bill of Lading",
    serviceIds: ["translation", "mfa-legalization", "trademark-ip", "company-boi"],
    tagMatch: ["customs", "logistics", "trade", "shipping"] },
  { slug: "marine-yacht", name: "Marine & Aviation", tagline: "Crew · Vessel · Charter",
    serviceIds: ["notary-public", "translation", "police-clearance", "apostille"],
    tagMatch: ["marine", "aviation", "crew", "vessel"] },
];

const CATEGORY_TO_SERVICE: Record<BlogCategory, ServiceId[]> = {
  Notary: ["notary-public", "mfa-legalization", "embassy-legalization", "apostille"],
  Translation: ["translation", "naati", "sworn-translation", "mfa-legalization"],
  Visa: ["visa-foreigners-thailand", "visa-thai-citizens", "ltr-elite-visa", "marriage-thai-foreign"],
  Legalization: ["mfa-legalization", "embassy-legalization", "apostille", "notary-public"],
  Legal: ["civil-family-probate", "criminal-defense", "real-estate-conveyancing", "tax-law"],
};

const SERVICE_TO_CATEGORIES: Record<ServiceId, BlogCategory[]> = {
  "translation": ["Translation", "Legalization"],
  "notary-public": ["Notary", "Legalization"],
  "naati": ["Translation"],
  "police-clearance": ["Legalization", "Visa"],
  "mfa-legalization": ["Legalization", "Notary"],
  "embassy-legalization": ["Legalization", "Notary"],
  "visa-thai-citizens": ["Visa"],
  "visa-foreigners-thailand": ["Visa"],
  "marriage-thai-foreign": ["Visa", "Legal"],
  "interpreter": ["Translation"],
  "apec-abtc": ["Visa"],
  "apostille": ["Legalization", "Notary"],
  "tm30-90day": ["Visa"],
  "work-permit-boi": ["Visa", "Legal"],
  "permanent-residence": ["Visa", "Legal"],
  "thai-citizenship": ["Visa", "Legal"],
  "company-boi": ["Legal"],
  "trademark-ip": ["Legal"],
  "sworn-translation": ["Translation", "Legal"],
  "criminal-defense": ["Legal"],
  "civil-family-probate": ["Legal"],
  "real-estate-conveyancing": ["Legal"],
  "tax-law": ["Legal"],
  "labor-employment": ["Legal"],
  "banking-finance": ["Legal"],
  "pdpa-cybersecurity": ["Legal"],
  "international-arbitration": ["Legal"],
  "ltr-elite-visa": ["Visa"],
  "ma-private-equity": ["Legal"],
  "work-experience-certificate": ["Legal", "Legalization", "Translation"],
  "medical-council-certification": ["Legal", "Legalization", "Translation"],
  "notary-public-foreigners": ["Notary", "Legalization"],
  "mfa-legalization-foreigners": ["Legalization", "Notary"],
  "marriage-abroad-thai": ["Visa", "Legal", "Legalization"],
  "naati-online-worldwide": ["Translation", "Visa"],
  "government-document-translation": ["Translation", "Legalization"],
  "government-document-certification": ["Legalization", "Legal"],
  "embassy-legalization-by-country": ["Legalization", "Notary"],
};

export type RelatedBundle = {
  services: ServiceMeta[];
  posts: BlogPost[];
  industries: typeof INDUSTRY_INDEX;
};

/** Build cross-section recommendations from a Service context. */
export function relatedForService(serviceId: ServiceId, limit = 6): RelatedBundle {
  const s = SERVICE_INDEX[serviceId];
  const cats = SERVICE_TO_CATEGORIES[serviceId] ?? [];
  const posts = BLOG_INDEX
    .filter((p) => cats.includes(p.category))
    .slice(0, limit);
  const services = s.relatedServiceIds.slice(0, limit).map((id) => SERVICE_INDEX[id]);
  const industries = INDUSTRY_INDEX
    .filter((i) => i.serviceIds.includes(serviceId))
    .slice(0, limit);
  return { services, posts, industries };
}

/** Build cross-section recommendations from a Blog context. */
export function relatedForBlog(slug: string, limit = 6): RelatedBundle {
  const post = BLOG_INDEX.find((p) => p.slug === slug);
  if (!post) return { services: [], posts: [], industries: [] };
  const serviceIds = CATEGORY_TO_SERVICE[post.category] ?? [];
  const services = serviceIds.slice(0, limit).map((id) => SERVICE_INDEX[id]);
  const tagSet = new Set(post.tags.map((t) => t.toLowerCase()));
  const industries = INDUSTRY_INDEX
    .filter((i) => i.tagMatch.some((m) => tagSet.has(m)) || i.serviceIds.some((id) => serviceIds.includes(id)))
    .slice(0, limit);
  const posts = BLOG_INDEX
    .filter((p) => p.slug !== slug && p.category === post.category)
    .slice(0, limit);
  return { services, posts, industries };
}
